// src/pages/Users/UsersList.jsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { usersApi } from '../../services/api';
import {
  Button,
  Input,
  Checkbox,
  NoticeModal,
  ConfirmModal,
  Badge,
  LineNumberTextarea,
  PageHeader,
  EmptyStateCard
} from '../../components/common';
import PageLayout from '../../components/PageLayout';
import { useIsMobile, usePageData } from '../../hooks';
import { parseApiError } from '../../utils';

// Импорт вашей дизайн-системы
import { pageStyles } from '../../styles/pageStyles';
import { colors, spacing, borderRadius, shadows, typography, zIndex } from '../../styles/theme';
import { inputBase, modalOverlay, modalCard, modalCardMobile } from '../../styles/commonStyles';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString('ru-RU');
  } catch (e) {
    return dateString;
  }
};

export default function UsersList() {
  const isMobile = useIsMobile();

  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null); 

  // ДОБАВЛЕН параметр ordering
  const [filters, setFilters] = useState({
    is_registered: '', 
    paid: '',
    ordering: '' // '' - по умолчанию, '-created_at' - сначала новые, 'created_at' - сначала старые
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [extrasText, setExtrasText] = useState('{}');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [cleanRegistrationsOpen, setCleanRegistrationsOpen] = useState(false);
  const [cleanPaymentsOpen, setCleanPaymentsOpen] = useState(false);
  const [cleanCheckboxesOpen, setCleanCheckboxesOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const [notice, setNotice] = useState({ open: false, type: 'success', text: '' });

  const [localUsers, setLocalUsers] = useState([]);

  const [checkboxStates, setCheckboxStates] = useState(() => {
    try {
      const dataStr = localStorage.getItem('user_checkboxes');
      return dataStr ? JSON.parse(dataStr) : {};
    } catch (e) {
      return {};
    }
  });

  const handleCheckboxChange = (userId, index) => {
    const nextStates = {
      ...checkboxStates,
      [userId]: {
        ...checkboxStates[userId],
        [index]: !checkboxStates[userId]?.[index]
      }
    };
    setCheckboxStates(nextStates);
    try {
      localStorage.setItem('user_checkboxes', JSON.stringify(nextStates));
    } catch (e) {
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== debouncedSearch) {
        setDebouncedSearch(searchQuery);
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterOpen(false);
      }
    };
    if (filterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterOpen]);

  const applyFilters = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
    setFilterOpen(false);
  };

  const fetchUsers = useCallback(() => {
    const params = { page };
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.is_registered !== '') params.is_registered = filters.is_registered;
    if (filters.paid !== '') params.paid = filters.paid;
    if (filters.ordering !== '') params.ordering = filters.ordering; // Отправляем сортировку

    return usersApi.list({ params });
  }, [page, debouncedSearch, filters]);

  const { data, loading, error, retry } = usePageData(fetchUsers, [page, debouncedSearch, filters]);

  useEffect(() => {
    if (data) {
      const results = data.results || data || [];
      setLocalUsers(results);
      setHasNext(!!data.next);
      setHasPrev(!!data.previous);
    }
  }, [data]);

  useEffect(() => {
    if (error && error.originalError?.response?.status === 404 && page > 1) {
      setPage(1);
    }
  }, [error, page]);

  const handleNext = () => { if (hasNext) setPage(p => p + 1); };
  const handlePrev = () => { if (hasPrev) setPage(p => Math.max(1, p - 1)); };

  const openEdit = (user) => {
    setModalError(null);
    setEditUser({ ...user });
    const extras = user?.extras && typeof user.extras === 'object' ? user.extras : {};
    setExtrasText(JSON.stringify(extras, null, 2));
    setEditOpen(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    setModalError(null);

    let extrasObj = {};
    try {
      extrasObj = extrasText.trim() ? JSON.parse(extrasText) : {};
    } catch (parseErr) {
      setModalError(`Ошибка JSON: ${parseErr.message}`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        username: editUser.username,
        first_name: editUser.first_name,
        last_name: editUser.last_name,
        email: editUser.email,
        phone: editUser.phone,
        is_registered: !!editUser.is_registered,
        paid: !!editUser.paid,
        extras: extrasObj,
      };

      await usersApi.update(editUser.id, payload);
      setEditOpen(false);
      setNotice({ open: true, type: 'success', text: `Пользователь ${editUser.username} обновлен` });
      retry();
    } catch (err) {
      setModalError(parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const formatExtras = () => {
    try {
      const obj = extrasText.trim() ? JSON.parse(extrasText) : {};
      setExtrasText(JSON.stringify(obj, null, 2));
      setModalError(null);
    } catch (e) {
      setModalError(`Невалидный JSON: ${e.message}`);
    }
  };

  const openDelete = (user) => {
    setDeleteUser(user);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteUser) return;
    setDeleting(true);
    try {
      await usersApi.remove(deleteUser.id);
      setDeleteOpen(false);
      setNotice({ open: true, type: 'success', text: 'Пользователь удален' });
      if (editOpen && editUser?.id === deleteUser.id) {
        setEditOpen(false);
      }
      retry();
    } catch (err) {
      setNotice({ open: true, type: 'error', text: parseApiError(err) });
    } finally {
      setDeleting(false);
    }
  };

  const handleBatchAction = async (actionType) => {
    setCleaning(true);
    try {
      if (actionType === 'registrations') {
        await usersApi.cleanRegistrations();
        setCleanRegistrationsOpen(false);
      } else if (actionType === 'payments') {
        await usersApi.cleanPayments();
        setCleanPaymentsOpen(false);
      } else if (actionType === 'checkboxes') {
        localStorage.removeItem('user_checkboxes');
        setCheckboxStates({});
        setCleanCheckboxesOpen(false);
      }
      setNotice({ open: true, type: 'success', text: 'Операция выполнена успешно' });
      retry();
    } catch (err) {
      setNotice({ open: true, type: 'error', text: parseApiError(err) });
    } finally {
      setCleaning(false);
    }
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: spacing.md,
    maxWidth: '100%',
    width: '100%',
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  return (
    <PageLayout loading={loading} error={error} onRetry={retry}>
      <div style={pageStyles.page}>
        <div style={pageStyles.container}>
          <PageHeader
            title="Пользователи"
            subtitle="Управление базой пользователей"
            isMobile={isMobile}
            actions={
              <div style={{ display: 'flex', gap: spacing.sm, flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                <Button variant="primary" onClick={() => setCleanRegistrationsOpen(true)} disabled={loading} fullWidth={isMobile}>
                  Сброс регистраций
                </Button>
                <Button variant="primary" onClick={() => setCleanPaymentsOpen(true)} disabled={loading} fullWidth={isMobile}>
                  Сброс оплат
                </Button>
                <Button variant="primary" onClick={() => setCleanCheckboxesOpen(true)} disabled={loading} fullWidth={isMobile}>
                  Сброс чекбоксов
                </Button>
              </div>
            }
          />

          <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.xl, alignItems: 'center', width: '100%' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={20} style={{ position: 'absolute', left: spacing.md, top: '50%', transform: 'translateY(-50%)', color: colors.gray400 }} />
              <input
                type="text"
                placeholder="Поиск пользователей..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  ...inputBase, 
                  paddingLeft: 44, 
                }}
              />
            </div>
            
            <div ref={filterRef} style={{ position: 'relative' }}>
              <Button variant="secondary" onClick={() => setFilterOpen(!filterOpen)} style={{ position: 'relative' }}>
                <Filter size={18} />
                {isMobile ? '' : 'Фильтр'}
                {activeFiltersCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -6, right: -6,
                    background: colors.primary, color: colors.white,
                    borderRadius: borderRadius.rounded, width: 20, height: 20,
                    fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700
                  }}>
                    {activeFiltersCount}
                  </span>
                )}
              </Button>

              {filterOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: spacing.xs,
                  width: isMobile ? 'calc(100vw - 32px)' : '300px',
                  background: colors.white,
                  borderRadius: borderRadius.large,
                  boxShadow: shadows.card,
                  border: `1px solid ${colors.gray200}`,
                  padding: spacing.lg,
                  zIndex: zIndex.modal,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.md,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs }}>
                    <span style={{ fontSize: typography.h3.fontSize, fontWeight: typography.h3.fontWeight }}>Фильтры</span>
                    <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.gray400 }}><X size={20}/></button>
                  </div>

                  <div>
                    <label style={pageStyles.label}>Статус регистрации</label>
                    <select 
                      value={filters.is_registered} 
                      onChange={(e) => setFilters({...filters, is_registered: e.target.value})}
                      style={{ ...inputBase, padding: `${spacing.sm}px ${spacing.md}px` }} 
                    >
                      <option value="">Все</option>
                      <option value="true">Зарегистрирован</option>
                      <option value="false">Не зарегистрирован</option>
                    </select>
                  </div>

                  {/* ИСПРАВЛЕННЫЙ БЛОК СОРТИРОВКИ */}
                  <div>
                    <label style={pageStyles.label}>Дата регистрации</label>
                    <select 
                      value={filters.ordering} 
                      onChange={(e) => setFilters({...filters, ordering: e.target.value})}
                      style={{ ...inputBase, padding: `${spacing.sm}px ${spacing.md}px` }}
                    >
                      <option value="">По умолчанию</option>
                      <option value="-created_at">Сначала новые (Недавно)</option>
                      <option value="created_at">Сначала старые (Давно)</option>
                    </select>
                  </div>

                  <div>
                    <label style={pageStyles.label}>Статус оплаты</label>
                    <select 
                      value={filters.paid} 
                      onChange={(e) => setFilters({...filters, paid: e.target.value})}
                      style={{ ...inputBase, padding: `${spacing.sm}px ${spacing.md}px` }}
                    >
                      <option value="">Все</option>
                      <option value="true">Оплачено</option>
                      <option value="false">Не оплачено</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.xs }}>
                     <Button variant="secondary" fullWidth onClick={() => applyFilters({ is_registered: '', paid: '', ordering: '' })}>Сбросить</Button>
                     <Button variant="primary" fullWidth onClick={() => applyFilters(filters)}>Применить</Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {localUsers.length === 0 ? (
            <EmptyStateCard title="Список пуст" description="Пользователи не найдены." isMobile={isMobile} />
          ) : (
            <>
              <div style={gridStyle}>
                {localUsers.map((user) => (
                  <UserCard
                    key={user.id}
                    user={user}
                    isMobile={isMobile}
                    onEdit={() => openEdit(user)}
                    checkboxValues={checkboxStates[user.id] || {}}
                    onCheckboxChange={handleCheckboxChange}
                  />
                ))}
              </div>

              <div style={styles.pagination}>
                <Button variant="secondary" disabled={!hasPrev || loading} onClick={handlePrev}>← Назад</Button>
                <div style={{ ...typography.body, color: colors.gray500 }}>{page}</div>
                <Button variant="secondary" disabled={!hasNext || loading} onClick={handleNext}>Вперед →</Button>
              </div>
            </>
          )}
        </div>

        <BaseModal open={editOpen} onClose={() => setEditOpen(false)} title="Редактирование пользователя" isMobile={isMobile}>
          <form onSubmit={handleSaveUser} style={pageStyles.form}>

            <div>
              <Input 
                label="Имя пользователя" 
                required 
                value={editUser?.username || ''} 
                onChange={(e) => setEditUser({ ...editUser, username: e.target.value })} 
              />
              {editUser?.created_at && (
                <div style={{ ...typography.helper, fontSize: 12, marginTop: spacing.xs }}>
                  {formatDate(editUser.created_at)}
                </div>
              )}
            </div>

            <div style={isMobile ? pageStyles.fieldRowMobile : pageStyles.fieldRow}>
               <Input label="Имя" required value={editUser?.first_name || ''} onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })} />
               <Input label="Фамилия" required value={editUser?.last_name || ''} onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })} />
            </div>

            <div style={isMobile ? pageStyles.fieldRowMobile : pageStyles.fieldRow}>
              <Input label="Email" type="email" value={editUser?.email || ''} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} />
              <Input label="Телефон" value={editUser?.phone || ''} onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
              <Checkbox checked={!!editUser?.is_registered} onChange={() => setEditUser({ ...editUser, is_registered: !editUser.is_registered })} title="Зарегистрирован" description="Пользователь прошел процесс регистрации" />
              <Checkbox checked={!!editUser?.paid} onChange={() => setEditUser({ ...editUser, paid: !editUser.paid })} title="Оплачено" description="Пользователь оплатил участие" />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs }}>
                <label style={pageStyles.label}>Дополнительные данные (JSON)</label>
                <Button variant="secondary" onClick={formatExtras} type="button" style={{ padding: '4px 10px', height: 'auto', fontSize: 12 }}>Форматировать</Button>
              </div>
              <LineNumberTextarea value={extrasText} onChange={(e) => setExtrasText(e.target.value)} rows={6} placeholder='{"key": "value"}' />
            </div>

            {modalError && <div style={{ background: colors.errorBg, color: colors.errorText, padding: spacing.md, borderRadius: borderRadius.medium, fontSize: 13 }}>{modalError}</div>}

            <div style={pageStyles.formActions}>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', justifyContent: 'space-between', gap: spacing.md }}>
                <Button variant="secondary" onClick={() => { setEditOpen(false); openDelete(editUser); }} type="button" fullWidth={isMobile} style={{color: colors.error}}>
                  Удалить
                </Button>
                <Button variant="primary" type="submit" loading={saving} fullWidth={isMobile}>
                  Сохранить изменения
                </Button>
              </div>
            </div>
          </form>
        </BaseModal>

        <ConfirmModal open={deleteOpen} title="Удаление пользователя" message={`Вы уверены, что хотите удалить ${deleteUser?.username}?`} description="Это действие нельзя отменить." confirmText="Удалить" cancelText="Отмена" loading={deleting} onConfirm={handleConfirmDelete} onClose={() => setDeleteOpen(false)} />
        <ConfirmModal open={cleanRegistrationsOpen} title="Сброс регистраций" message="Сбросить все регистрации?" confirmText="Сбросить" loading={cleaning} onConfirm={() => handleBatchAction('registrations')} onClose={() => setCleanRegistrationsOpen(false)} />
        <ConfirmModal open={cleanPaymentsOpen} title="Сбросить все оплаты" message="Отменить все оплаты пользователей?" description="Это действие нельзя отменить" confirmText="Сбросить" loading={cleaning} onConfirm={() => handleBatchAction('payments')} onClose={() => setCleanPaymentsOpen(false)} />
        <ConfirmModal open={cleanCheckboxesOpen} title="Сброс чекбоксов" message="Сбросить все отмеченные чекбоксы пользователей?" description="Это действие нельзя отменить" confirmText="Сбросить" loading={cleaning} onConfirm={() => handleBatchAction('checkboxes')} onClose={() => setCleanCheckboxesOpen(false)} />
        <NoticeModal open={notice.open} type={notice.type} text={notice.text} onClose={() => setNotice({ ...notice, open: false })} />
      </div>
    </PageLayout>
  );
}

function BaseModal({ open, title, onClose, children, isMobile }) {
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setTimeout(() => setShow(true), 10);
      document.body.style.overflow = 'hidden';
    } else {
      setShow(false);
      const timer = setTimeout(() => {
        setMounted(false);
        document.body.style.overflow = '';
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!mounted) return null;

  return (
    <div
      style={{ ...modalOverlay, opacity: show ? 1 : 0, transition: 'opacity 0.3s ease' }}
      onClick={(e) => { if(e.target === e.currentTarget) onClose(); }}
    >
      <div 
        style={{ 
          ...modalCard, 
          ...(isMobile ? modalCardMobile : {}),
          transform: show ? 'scale(1)' : 'scale(0.95)',
          transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
        }} 
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          padding: `${spacing.lg}px ${spacing.xl}px`, borderBottom: `1px solid ${colors.gray200}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, ...typography.h3 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: colors.gray400, padding: 4, display: 'flex' }}>
            <X size={24} />
          </button>
        </div>
        <div style={{ padding: spacing.xl, overflowY: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function UserCard({ user, isMobile, onEdit, checkboxValues, onCheckboxChange }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        ...pageStyles.card, 
        padding: spacing.md,
        marginBottom: 0,
        transform: isHovered && !isMobile ? 'translateY(-2px)' : 'none',
        boxShadow: isHovered && !isMobile ? shadows.cardHover : shadows.card, 
        border: `1px solid ${colors.gray100}`,
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onEdit}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, marginBottom: spacing.md }}>
        <div style={{ color: colors.primary }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
            <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0m-9 8c0 1 1 1 1 1h5.256A4.5 4.5 0 0 1 8 12.5a4.5 4.5 0 0 1 1.544-3.393Q8.844 9.002 8 9c-5 0-6 3-6 4m9.886-3.54c.18-.613 1.048-.613 1.229 0l.043.148a.64.64 0 0 0 .921.382l.136-.074c.561-.306 1.175.308.87.869l-.075.136a.64.64 0 0 0 .382.92l.149.045c.612.18.612 1.048 0 1.229l-.15.043a.64.64 0 0 0-.38.921l.074.136c.305.561-.309 1.175-.87.87l-.136-.075a.64.64 0 0 0-.92.382l-.045.149c-.18.612-1.048.612-1.229 0l-.043-.15a.64.64 0 0 0-.921-.38l-.136.074c-.561.305-1.175-.309-.87-.87l.075-.136a.64.64 0 0 0-.382-.92l-.148-.045c-.613-.18-.613-1.048 0-1.229l.148-.043a.64.64 0 0 0 .382-.921l-.074-.136c-.306-.561.308-1.175.869-.87l.136.075a.64.64 0 0 0 .92-.382zM14 12.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ ...typography.body, fontWeight: 600, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
              { user.first_name || 'Гость'} {user.last_name}
            </span>
            {user.is_superuser && <label style={{...typography.helper, fontSize: 12}}>Администратор</label>}
            <label style={{...typography.helper, fontSize: 12}}>@{user.username || 'unknown'}</label>
            
            <label style={{...typography.helper, fontSize: 11, marginTop: 2, color: colors.gray400}}>
              {formatDate(user.created_at)}
            </label>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, flexShrink: 0, cursor: 'pointer' }} onClick={(e) => e.stopPropagation()}>
          <Checkbox
            style={{ border: 'none', background: 'none', padding: 0 }}
            checked={!!checkboxValues[0]}
            onChange={() => onCheckboxChange(user.id, 0)}
          />
          <Checkbox
            style={{ border: 'none', background: 'none', padding: 0 }}
            checked={!!checkboxValues[1]}
            onChange={() => onCheckboxChange(user.id, 1)}
          />
          <Checkbox
            style={{ border: 'none', background: 'none', padding: 0 }}
            checked={!!checkboxValues[2]}
            onChange={() => onCheckboxChange(user.id, 2)}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, marginBottom: spacing.md, background: colors.gray100, padding: spacing.sm, borderRadius: borderRadius.medium }}>
        <InfoRow label="EMAIL" value={user.email} />
        <InfoRow label="ТЕЛЕФОН" value={user.phone} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.xs, marginTop: 'auto' }}>
        <Badge variant={user.is_registered ? 'success' : 'inactive'} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>Регистрация</Badge>
        <Badge variant={user.paid ? 'warning' : 'inactive'} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>Оплата</Badge>
      </div>
    </div>
  );
}

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    <span style={{ color: colors.gray400, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em' }}>{label}</span>
    <span style={{ color: colors.primary, fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || '—'}</span>
  </div>
);

const styles = {
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: spacing.xl, gap: spacing.md, paddingBottom: spacing.xl },
};